import { ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, OnInit, computed, inject, input, output, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { ToastrService } from 'ngx-toastr';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { SelectModule } from 'primeng/select';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { TooltipModule } from 'primeng/tooltip';
import { Subject, catchError, debounceTime, distinctUntilChanged, finalize, interval, of } from 'rxjs';

import { IChatwootInboxItem, esBotApagado } from 'src/app/shared/interfaces/IChatwoot';
import { ChatGlobalPanelStateService } from 'src/app/shared/services/chat-global-panel-state.service';
import { ChatwootNotificationService } from 'src/app/shared/services/chatwoot-notification.service';
import { ChatwootService } from 'src/app/shared/services/chatwoot.service';

/** Refresco automático de la bandeja (ms). */
const REFRESH_INTERVAL_MS = 30_000;
/** Debounce de la búsqueda (ms). */
const SEARCH_DEBOUNCE_MS = 400;

interface IEstadoOption {
    label: string;
    value: string;
}

/** Modo de renderizado de la lista: página completa o panel lateral compacto (FAB). */
export type ChatConversacionesListaModo = 'pagina' | 'panel';

/**
 * Lista reutilizable de conversaciones del inbox de WhatsApp (Chatwoot).
 * Lista custom tipo inbox (no mi-table-filter: filas ricas con avatar, preview y chips).
 * - modo 'pagina': layout amplio con todos los filtros (página Chat global).
 * - modo 'panel': compacto para el panel lateral del FAB (búsqueda + solo no leídos + solo mías).
 * Click en una fila → abre el drawer de chat por teléfono (sin requerir candidato) y
 * emite `conversacionAbierta` (el panel del FAB se cierra al abrir el chat).
 */
@Component({
    selector: 'app-chat-conversaciones-lista',
    standalone: true,
    imports: [
        FormsModule,
        ButtonModule,
        InputTextModule,
        ProgressSpinnerModule,
        SelectModule,
        ToggleSwitchModule,
        TooltipModule
    ],
    templateUrl: './chat-conversaciones-lista.html',
    styleUrl: './chat-conversaciones-lista.scss',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ChatConversacionesListaComponent implements OnInit {
    private readonly chatwootService = inject(ChatwootService);
    private readonly notificationService = inject(ChatwootNotificationService);
    private readonly panelState = inject(ChatGlobalPanelStateService);
    private readonly toastr = inject(ToastrService);
    private readonly cdr = inject(ChangeDetectorRef);
    private readonly destroyRef = inject(DestroyRef);

    /** Modo de presentación: 'pagina' (Chat global) o 'panel' (FAB lateral compacto). */
    readonly modo = input<ChatConversacionesListaModo>('pagina');

    /** Se emite tras abrir el drawer de chat de una conversación (para cerrar el panel del FAB). */
    readonly conversacionAbierta = output<IChatwootInboxItem>();

    // ─── Estado ──────────────────────────────────────────────────────────────
    readonly cargando = signal(false);
    readonly cargandoMas = signal(false);
    readonly items = signal<IChatwootInboxItem[]>([]);
    readonly totalNoLeidos = signal(0);
    readonly page = signal(1);
    /** true cuando "Cargar más" ya no trajo resultados nuevos. */
    readonly finLista = signal(false);

    // ─── Filtros ─────────────────────────────────────────────────────────────
    readonly estadoOptions: IEstadoOption[] = [
        { label: 'Abiertas', value: 'open' },
        { label: 'Resueltas', value: 'resolved' },
        { label: 'Todas', value: 'all' }
    ];
    estado = 'open';
    soloNoLeidos = false;
    busqueda = '';
    /** Filtro server-side: solo conversaciones de MIS campañas (default ON; "Todas" para supervisión). */
    soloMias = true;
    /** Filtro client-side: solo conversaciones originadas por campaña. */
    readonly soloCampanas = signal(false);

    private readonly busqueda$ = new Subject<string>();

    /** Filas visibles: aplica el filtro client-side "Solo campañas". */
    readonly conversaciones = computed<IChatwootInboxItem[]>(() => {
        const items = this.items();
        return this.soloCampanas() ? items.filter((i) => i.campanaId != null) : items;
    });

    constructor() {
        // Búsqueda con debounce → recarga page 1
        this.busqueda$
            .pipe(debounceTime(SEARCH_DEBOUNCE_MS), distinctUntilChanged(), takeUntilDestroyed())
            .subscribe(() => this.recargar());

        // Refresco automático de la primera página
        interval(REFRESH_INTERVAL_MS)
            .pipe(takeUntilDestroyed())
            .subscribe(() => this.refrescarSilencioso());
    }

    ngOnInit(): void {
        // El panel del FAB destruye la lista al cerrarse: si hay snapshot vigente
        // (volviendo desde un chat con "←"), se hidrata al instante y se refresca
        // en silencio, en vez de mostrar "Cargando conversaciones…" otra vez.
        const snap = this.modo() === 'panel' ? this.panelState.obtener() : null;
        if (snap) {
            this.estado = snap.estado;
            this.soloNoLeidos = snap.soloNoLeidos;
            this.busqueda = snap.busqueda;
            this.soloMias = snap.soloMias;
            this.soloCampanas.set(snap.soloCampanas);
            this.items.set(snap.items);
            this.totalNoLeidos.set(snap.totalNoLeidos);
            this.page.set(snap.page);
            this.finLista.set(snap.finLista);
            this.refrescarSilencioso();
            return;
        }
        this.recargar();
    }

    // ─── Carga ───────────────────────────────────────────────────────────────

    recargar(): void {
        this.finLista.set(false);
        this.cargar(1, { append: false, silent: false });
    }

    cargarMas(): void {
        if (this.cargando() || this.cargandoMas()) return;
        this.cargar(this.page() + 1, { append: true, silent: false });
    }

    onBusquedaChange(value: string): void {
        this.busqueda = value;
        this.busqueda$.next(value.trim());
    }

    onFiltroChange(): void {
        this.recargar();
    }

    private refrescarSilencioso(): void {
        if (this.cargando() || this.cargandoMas()) return;
        this.finLista.set(false);
        this.cargar(1, { append: false, silent: true });
    }

    private cargar(page: number, opts: { append: boolean; silent: boolean }): void {
        if (opts.append) this.cargandoMas.set(true);
        else if (!opts.silent) this.cargando.set(true);

        this.chatwootService
            .getInboxConversations(this.estado, page, this.soloNoLeidos, this.busqueda.trim(), this.soloMias)
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                catchError(() => {
                    if (!opts.silent) this.toastr.error('No fue posible cargar las conversaciones.', 'Chat global');
                    return of(null);
                }),
                finalize(() => {
                    this.cargando.set(false);
                    this.cargandoMas.set(false);
                    this.cdr.markForCheck();
                })
            )
            .subscribe((res) => {
                if (!res?.success || !res.data) return;
                const nuevos = res.data.items ?? [];
                this.totalNoLeidos.set(res.data.totalNoLeidos ?? 0);
                // Basado en la página cruda de Chatwoot (hayMas), no en los items ya
                // filtrados: una página puede quedar vacía tras filtrar y aun así haber más.
                this.finLista.set(!res.data.hayMas);

                if (opts.append) {
                    this.page.set(page);
                    if (nuevos.length > 0) {
                        this.items.update((curr) => {
                            const vistos = new Set(curr.map((i) => i.conversationId));
                            return [...curr, ...nuevos.filter((i) => !vistos.has(i.conversationId))];
                        });
                    }
                } else {
                    this.page.set(1);
                    this.items.set(nuevos);
                }
                this.guardarSnapshot();
            });
    }

    /** Persiste el estado de la lista para hidratarla al volver desde un chat (solo modo panel). */
    private guardarSnapshot(): void {
        if (this.modo() !== 'panel') return;
        this.panelState.guardar({
            items: this.items(),
            totalNoLeidos: this.totalNoLeidos(),
            page: this.page(),
            finLista: this.finLista(),
            estado: this.estado,
            soloNoLeidos: this.soloNoLeidos,
            busqueda: this.busqueda,
            soloMias: this.soloMias,
            soloCampanas: this.soloCampanas(),
            guardadoEn: Date.now()
        });
    }

    // ─── Acciones ────────────────────────────────────────────────────────────

    abrirChat(item: IChatwootInboxItem): void {
        if (!item.contactPhone) {
            this.toastr.warning('La conversación no tiene un teléfono asociado.', 'Chat global');
            return;
        }
        // desdePanelFab: en modo panel el drawer muestra "← volver al listado".
        this.notificationService.openByPhone(item.contactPhone, this.nombreMostrado(item), this.modo() === 'panel');
        this.conversacionAbierta.emit(item);
    }

    // ─── Presentación ────────────────────────────────────────────────────────

    nombreMostrado(item: IChatwootInboxItem): string {
        return item.contactName?.trim() || item.leadNombre?.trim() || item.contactPhone || `Conversación #${item.conversationId}`;
    }

    inicial(item: IChatwootInboxItem): string {
        const nombre = this.nombreMostrado(item);
        const letra = nombre.trim().charAt(0).toUpperCase();
        return /[A-ZÁÉÍÓÚÑ]/.test(letra) ? letra : '#';
    }

    ultimoMensajeDisplay(item: IChatwootInboxItem): string {
        const texto = item.ultimoMensaje?.trim() ?? '';
        if (!texto) return 'Sin mensajes';
        return item.esEntrante ? texto : `→ ${texto}`;
    }

    /** Bot ON por defecto: solo se apaga si estadoBot dice explícitamente lo contrario (regla compartida esBotApagado). */
    botActivo(item: IChatwootInboxItem): boolean {
        return !esBotApagado(item.estadoBot);
    }

    /** Hora relativa desde epoch en SEGUNDOS (mismo formato de fecha que usa el drawer: epoch * 1000). */
    horaRelativa(epochSeconds?: number): string {
        if (!epochSeconds || epochSeconds <= 0) return '';
        const diffMin = Math.floor((Date.now() - epochSeconds * 1000) / 60_000);
        if (diffMin < 1) return 'Ahora';
        if (diffMin < 60) return `hace ${diffMin} min`;
        const horas = Math.floor(diffMin / 60);
        if (horas < 24) return `hace ${horas} h`;
        const dias = Math.floor(horas / 24);
        if (dias === 1) return 'Ayer';
        if (dias < 7) return `hace ${dias} días`;
        return new Date(epochSeconds * 1000).toLocaleDateString('es-CO', { day: '2-digit', month: 'short', year: 'numeric' });
    }
}
